import {
  SequenceReadable,
  sequencesDestroyMutation,
  sequencesDuplicateRetrieveOptions,
  sequencesListOptions,
  sequencesListQueryKey
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import { CampaignsForm } from '@crm/features/campaigns/components'
import { stats, textType } from '@crm/features/campaigns/utils'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'

import { defaultFilters } from '@crm/utils'
import {
  ConditionalText,
  TableActions,
  TableContainer,
  TitleSection
} from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Flex, Modal, notification, Space, Typography } from 'antd'
import { BaseType } from 'antd/es/typography/Base'
import { Copy, Edit2, Trash } from 'iconsax-react'
import { useEffect, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const { Text } = Typography

const formName = 'campaigns-form'

export const Campaigns = () => {
  const { t } = useLocales()
  const params = useQueryParams()

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()
  const navigate = useAppNavigate()

  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [isCreate, setIsCreate] = useState(false)
  const [duplicateId, setDuplicateId] = useState<number>()

  const { data: campaigns, isLoading } = useQuery({
    ...sequencesListOptions({ query: { ...defaultFilters, ...params } })
  })

  const { data: duplicate, isLoading: isDuplicateLoading } = useQuery({
    ...sequencesDuplicateRetrieveOptions({ path: { id: Number(duplicateId) } }),
    enabled: !!duplicateId
  })

  useEffect(() => {
    if (duplicate) {
      setDuplicateId(undefined)
      invalidateQueries([sequencesListQueryKey()])
    }
  }, [duplicate, invalidateQueries])

  const onDuplicate = (id: number): void => setDuplicateId(id)

  const deleteCampaignsMutation = useMutation({
    ...sequencesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([sequencesListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onOpenModal = () => setIsCreate(true)

  const onDelete = () => {
    if (deleteItem) {
      deleteCampaignsMutation.mutate({ path: { id: deleteItem } })
    }
  }

  const onRow = ({ id }: SequenceReadable) => ({
    onClick: () => navigate(`/campaigns/${id}`)
  })

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setIsCreate(false)
    setEditItem(undefined)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useMemo(
    () => (id) =>
      [
        {
          label: t('campaigns.edit'),
          key: 'edit',
          icon: <Edit2 size={20} />,
          onClick: () => setEditItem(id)
        },
        {
          label: t('campaigns.duplicate'),
          key: 'duplicate',
          icon: <Copy size={20} />,
          onClick: () => onDuplicate(id)
        },
        { type: 'divider' },
        {
          label: t('campaigns.delete'),
          key: 'delete',
          danger: true,
          icon: <Trash size={20} />,
          onClick: () => setDeleteItem(id)
        }
      ],
    [t]
  )

  const columns: TableColumnsType<SequenceReadable> = useMemo(
    () => [
      {
        dataIndex: 'title',
        render: (title) => (
          <ConditionalText condition={title}>{title}</ConditionalText>
        )
      },
      {
        width: '350px',
        render: (campaign) => (
          <Flex gap={30}>
            {stats.map((i) => (
              <Text
                key={i}
                type={textType[i as keyof typeof textType] as BaseType}
              >
                <Space align="center" direction="vertical" size={0}>
                  {t(`campaigns.${i}`)}
                  {campaign[i]}
                </Space>
              </Text>
            ))}
          </Flex>
        )
      },
      {
        width: 50,
        fixed: 'right',
        key: 'actions',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TitleSection
        title={t(`menu.campaigns`)}
        count={campaigns?.count}
        isLoading={isLoading || isDuplicateLoading}
      />

      <TableContainer
        loading={isLoading || isDuplicateLoading}
        rowClassName="pointer"
        showSorterTooltip={false}
        data={campaigns}
        isHoverAlternative
        columns={columns}
        onRow={onRow}
        rightChildren={
          <Button type="primary" onClick={onOpenModal}>
            {t('campaigns.add_campaign')}
          </Button>
        }
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <Modal
        open={!!(editItem || isCreate)}
        destroyOnHidden
        title={t(
          isCreate ? 'campaigns.add_campaign' : 'campaigns.edit_campaign'
        )}
        onCancel={onCloseModal}
        cancelText={t('buttons.cancel')}
        okButtonProps={{ htmlType: 'submit', form: formName }}
        okText={t(isCreate ? 'buttons.add' : 'buttons.edit')}
      >
        <CampaignsForm
          id={editItem}
          formName={formName}
          onClose={onCloseModal}
        />
      </Modal>
    </>
  )
}
